import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { Lock, PlayCircle, Camera, Apple, Dumbbell, ChevronRight, Home, MessageCircle, Pill, Bell, GraduationCap, Gift, Copy, Download, Brain, Flame, Trophy, Star, StretchHorizontal, ShoppingBag } from "lucide-react";
import { InstallApp } from "@/components/InstallApp";
import { PdfViewerModal } from "@/components/PdfViewerModal";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function PushToggle() {
  const { t } = useLang();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then(setSub).catch(() => {});
  }, []);

  const subscribe = async () => {
    if (Notification.permission === "denied") { toast.error(t("push.denied")); return; }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") return;
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-key");
      const s = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.publicKey) });
      await api.post("/push/subscribe", s.toJSON());
      setSub(s);
      toast.success(t("push.on"));
    } catch (e) {
      toast.error(t("push.error"));
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch {}
    try { await sub.unsubscribe(); } catch {}
    setSub(null);
    setBusy(false);
  };

  if (!supported) return null;
  return (
    <div className="kc-card p-4 flex items-center gap-3 kc-animate" data-testid="push-toggle-card">
      <Bell className="w-5 h-5 text-primary shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block font-bold text-sm">{t("push.title")}</span>
        <span className="block text-xs text-muted-foreground">{t("push.sub")}</span>
      </span>
      <button data-testid="push-toggle-button" onClick={sub ? unsubscribe : subscribe} disabled={busy}
        className="kc-btn-primary !px-4 !py-2 text-xs shrink-0">
        {sub ? t("push.disable") : t("push.enable")}
      </button>
    </div>
  );
}

export function ReferralCard() {
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/client/referral").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      toast.success(t("ref.copied"));
    } catch {
      window.prompt(data.link, data.link);
    }
  };

  return (
    <div className="kc-card p-4 kc-animate" data-testid="referral-card">
      <div className="flex items-center gap-3">
        <Gift className="w-5 h-5 text-primary shrink-0" />
        <span className="flex-1 min-w-0">
          <span className="block font-bold text-sm">{t("ref.title")}</span>
          <span className="block text-xs text-muted-foreground">{t("ref.sub")}</span>
        </span>
        <button data-testid="referral-copy-button" onClick={copy}
          className="kc-btn-primary !px-4 !py-2 text-xs shrink-0 inline-flex items-center gap-1.5">
          <Copy className="w-3.5 h-3.5" /> {t("ref.copy")}
        </button>
      </div>
      {(data.credits > 0 || data.invited > 0) && (
        <p className="text-xs text-primary font-semibold mt-2" data-testid="referral-stats">
          {t("ref.invited")}: {data.invited} · {t("ref.creditsCount")}: {data.credits} (−20€)
        </p>
      )}
    </div>
  );
}

export function clientNav(t) {  return [
    { to: "/app", key: "home", label: t("nav.home"), end: true },
    { to: "/app/track", key: "track", label: t("nav.track") },
    { to: "/app/history", key: "history", label: t("nav.history") },
    { to: "/app/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/app/photos", key: "photos", label: t("nav.photos") },
    { to: "/app/packages", key: "packages", label: t("nav.packages") },
    { to: "/app/chat", key: "chat", label: t("nav.chat") },
    { to: "/app/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/app/education", key: "education", label: t("nav.education") },
    { to: "/app/meditation", key: "meditation", label: t("nav.meditation") },
    { to: "/app/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/app/shop", key: "shop", label: t("nav.shop") },
  ];
}

export function ClientLayout({ children }) {
  const { t } = useLang();
  const tabs = [
    { to: "/app", key: "tab-home", icon: Home, label: t("nav.tabHome"), end: true },
    { to: "/app/track", key: "tab-track", icon: PlayCircle, label: t("nav.tabTrack") },
    { to: "/app/nutrition", key: "tab-nutrition", icon: Apple, label: t("nav.tabNutrition") },
    { to: "/app/photos", key: "tab-photos", icon: Camera, label: t("nav.tabPhotos") },
    { to: "/app/chat", key: "tab-chat", icon: MessageCircle, label: t("nav.tabChat") },
    { to: "/app/packages", key: "tab-packages", icon: Dumbbell, label: t("nav.tabPackages") },
    { to: "/app/meditation", key: "tab-meditation", icon: Brain, label: t("nav.tabMeditation") },
    { to: "/app/stretching", key: "tab-stretching", icon: StretchHorizontal, label: t("nav.stretching") },
  ];
  return <Layout nav={clientNav(t)} tabs={tabs} dark>{children}</Layout>;
}

export function WeightChart({ entries }) {
  const pts = (entries || []).filter((e) => e.weight_kg != null).slice().reverse();
  if (pts.length < 2) return null;
  const W = 320, H = 90, PAD = 10;
  const vals = pts.map((p) => p.weight_kg);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const x = (i) => PAD + (i / (pts.length - 1)) * (W - 2 * PAD);
  const y = (v) => H - PAD - ((v - min) / range) * (H - 2 * PAD);
  const line = pts.map((p, i) => `${x(i)},${y(p.weight_kg)}`).join(" ");
  return (
    <div className="mt-3" data-testid="weight-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24">
        <polyline points={line} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.weight_kg)} r="3.5" fill="hsl(var(--accent))" />
        ))}
      </svg>
      <div className="flex justify-between text-xs text-muted-foreground font-mono">
        <span>{pts[0].date}</span>
        <span>{min.toFixed(1)}–{max.toFixed(1)} kg</span>
        <span>{pts[pts.length - 1].date}</span>
      </div>
    </div>
  );
}

export function youtubeEmbedUrl(url = "") {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export function isExternalMediaLink(url = "") {
  return /^https?:\/\//i.test(url) && !/\.(gif|mp4|webm|mov|png|jpe?g|webp)(\?|#|$)/i.test(url) && !youtubeEmbedUrl(url);
}

export function ExerciseMedia({ url, className = "", lazy = false }) {
  if (!url) return null;
  const yt = youtubeEmbedUrl(url);
  if (yt) {
    return (
      <div className={`${className} aspect-video overflow-hidden !p-0`}>
        <iframe src={yt} title="Video esercizio" data-testid="exercise-media" className="w-full h-full" style={{ border: 0 }}
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  if (/\.(mp4|webm|mov)(\?|#|$)/i.test(url)) {
    if (lazy) {
      return <video src={url} preload="metadata" controls playsInline className={className} data-testid="exercise-media" />;
    }
    return <video src={url} autoPlay loop muted playsInline className={className} data-testid="exercise-media" />;
  }
  return <img src={url} alt="" loading="lazy" className={className} data-testid="exercise-media" />;
}

export function BlockedBanner({ user }) {
  const { t } = useLang();
  const neverActive = !!user && !user.package_expires_at && !user.active_package;
  const trialExpired = !!user && !!user.package_expires_at && !user.active_package;
  const title = neverActive ? t("blocked.welcomeTitle") : trialExpired ? t("blocked.trialTitle") : t("blocked.title");
  const text = neverActive ? t("blocked.welcomeText") : trialExpired ? t("blocked.trialText") : t("blocked.text");
  const cta = neverActive || trialExpired ? t("blocked.chooseCta") : t("blocked.cta");
  return (
    <div className="kc-card border-destructive/50 p-10 text-center relative overflow-hidden" data-testid="package-expired-lock-notice">
      <Lock className="w-12 h-12 text-destructive mx-auto mb-4" />
      <h2 className="text-2xl font-black uppercase mb-2" data-testid="blocked-title">{title}</h2>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">{text}</p>
      <Link data-testid="renew-package-button" to="/app/packages" className="kc-btn-primary inline-block">{cta}</Link>
    </div>
  );
}

export function flattenWeeks(phase) {
  if (!phase) return [];
  return (phase.months || []).flatMap((m) => (m.weeks || []).map((w) => ({ ...w, monthName: m.name })));
}

export default function ClientHome() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [openWeek, setOpenWeek] = useState(null);
  const [measures, setMeasures] = useState([]);
  const [pdfUrl, setPdfUrl] = useState("");
  const [activity, setActivity] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const [reviewInfo, setReviewInfo] = useState(null);

  useEffect(() => {
    api.get("/client/overview").then((r) => setData(r.data));
    api.get("/client/measurements").then((r) => setMeasures(r.data)).catch(() => {});
    api.get("/client/review").then((r) => setReviewInfo(r.data)).catch(() => {});
    api.get("/client/activity-summary").then((r) => {
      setActivity(r.data);
      const s = r.data?.streak || 0;
      if (s >= 3 && !localStorage.getItem(`celebrated_streak_${s}`)) {
        localStorage.setItem(`celebrated_streak_${s}`, "1");
        setCelebration(s);
      }
    }).catch(() => {});
  }, []);

  if (!data) return <ClientLayout><p className="text-muted-foreground">{t("common.loading")}</p></ClientLayout>;

  const { user, coach, program, current_phase_idx, current_week_idx } = data;
  const daysLeft = user.package_expires_at
    ? Math.max(0, Math.ceil((new Date(user.package_expires_at) - Date.now()) / 86400000))
    : 0;

  if (user.blocked) return <ClientLayout><BlockedBanner user={user} /></ClientLayout>;

  const isTrial = !user.active_package;
  const phase = program?.phases?.[current_phase_idx];
  const weeks = flattenWeeks(phase);
  const currentWeek = weeks[current_week_idx];
  const progress = weeks.length ? Math.round(((current_week_idx + 1) / weeks.length) * 100) : 0;

  const tiles = [
    { to: "/app/track", img: "/emblem_track.png", label: t("nav.track"), sub: t("client.trackSub"), testid: "tile-track" },
    { to: "/app/photos", img: "/emblem_photo.png", label: t("nav.photos"), sub: t("client.photosSub"), testid: "tile-photos" },
    { to: "/app/history", img: "/emblem_history.png", label: t("nav.history"), sub: t("client.historySub"), testid: "tile-history" },
  ];

  const rows = [
    { img: "/emblem_workout.png", title: "Workout", sub: `${phase?.name || "—"} · ${currentWeek?.name || ""}`, testid: "row-workout", scroll: true },
    { to: "/app/nutrition", img: "/emblem_nutrition.png", title: t("nav.nutrition"), sub: t("client.nutritionSub"), testid: "row-nutrition" },
    { to: "/app/feedback", img: "/emblem_track.png", title: t("fb.title"), sub: t("fb.sub"), testid: "row-feedback" },
    ...(reviewInfo?.eligible ? [{ to: "/app/review", icon: Star, title: t("rv.leaveTitle"), sub: reviewInfo.review ? t("rv.myTitle") : t("rv.leaveSub"), testid: "row-review" }] : []),
    { to: "/app/library", icon: Dumbbell, title: t("lib.title"), sub: t("lib.sub"), testid: "row-library" },
    { to: "/app/chat", icon: MessageCircle, title: t("chat.title"), sub: t("chat.sub"), testid: "row-chat" },
    { to: "/app/supplements", icon: Pill, title: t("nav.supplements"), sub: t("supp.sub"), testid: "row-supplements" },
    { to: "/app/stretching", icon: StretchHorizontal, title: t("st.title"), sub: t("st.sub"), testid: "row-stretching" },
    { to: "/app/shop", icon: ShoppingBag, title: t("nav.shop"), sub: t("shop.sub"), testid: "row-shop" },
    { to: "/app/packages", img: "/emblem_packages.png", title: t("nav.packages"), sub: `${daysLeft} ${t("client.daysLeft")}`, testid: "row-packages" },
    { to: "/app/education", icon: GraduationCap, title: t("edu.title"), sub: t("edu.sub"), testid: "row-education" },
  ];

  const isLastWeek = !program?.is_sample && weeks.length > 0 && current_week_idx === weeks.length - 1;

  return (
    <ClientLayout>
      <div data-testid="client-home" className="space-y-8 max-w-2xl mx-auto">
        <PdfViewerModal url={pdfUrl} title={program?.name || t("client.downloadPdf")} onClose={() => setPdfUrl("")} />
        {user?.health_status === "red" && (
          <div className="kc-card border-destructive/50 p-5 flex items-start gap-3 kc-animate" data-testid="health-red-banner">
            <span className="w-2.5 h-2.5 rounded-full bg-destructive shrink-0 mt-1.5" />
            <p className="text-sm text-muted-foreground">{t("hc.bannerRed")}</p>
          </div>
        )}
        {activity && (activity.streak > 0 || activity.workouts_week > 0 || activity.diet_days_week > 0) && (
          <section className="kc-card-dark p-5" data-testid="progress-card">
            <p className="kc-eyebrow !text-[#E0597F] mb-4">{t("prog.title")}</p>
            <div className="flex items-center gap-3">
              <Flame className="w-9 h-9 text-accent shrink-0" />
              <div>
                <p className="font-mono text-4xl font-black leading-none" data-testid="streak-value">{activity.streak}</p>
                <p className="text-[10px] uppercase tracking-wider opacity-60 mt-1">{t("prog.streak")}</p>
              </div>
              {activity.best_streak > activity.streak && (
                <div className="ml-auto flex items-center gap-1.5 text-xs opacity-70">
                  <Trophy className="w-4 h-4 text-accent" />
                  <span className="font-mono font-bold">{activity.best_streak}</span> {t("prog.best")}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div data-testid="progress-workouts">
                <div className="flex justify-between text-xs mb-1">
                  <span className="opacity-70">{t("prog.workoutsWeek")}</span>
                  <span className="font-mono font-bold">{activity.workouts_week}/5</span>
                </div>
                <div className="h-2 rounded-full bg-secondary/60 overflow-hidden">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min(100, activity.workouts_week / 5 * 100)}%` }} />
                </div>
              </div>
              <div data-testid="progress-diet">
                <div className="flex justify-between text-xs mb-1">
                  <span className="opacity-70">{t("prog.dietDays")}</span>
                  <span className="font-mono font-bold">{activity.diet_days_week}/7</span>
                </div>
                <div className="h-2 rounded-full bg-secondary/60 overflow-hidden">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min(100, activity.diet_days_week / 7 * 100)}%` }} />
                </div>
              </div>
            </div>
          </section>
        )}
        {celebration && (
          <div className="fixed inset-0 z-[110] grid place-items-center bg-black/75 backdrop-blur-sm overflow-hidden"
            data-testid="celebration-overlay" onClick={() => setCelebration(null)}>
            {[...Array(18)].map((_, i) => (
              <span key={i} className="kc-confetti" style={{
                left: `${(i * 53) % 100}%`,
                background: ["#E0597F", "#9C2745", "#E8C15A", "#F5E6E8"][i % 4],
                animationDelay: `${(i % 6) * 0.3}s`,
              }} />
            ))}
            <div className="kc-card-dark p-8 text-center max-w-xs mx-4 kc-animate relative z-10">
              <Flame className="w-14 h-14 text-accent mx-auto mb-3" />
              <p className="text-4xl font-black font-mono" data-testid="celebration-streak">{celebration}</p>
              <p className="font-black uppercase tracking-wider mt-1">{t("prog.daysRow")}</p>
              <p className="text-muted-foreground text-sm mt-3">{t("prog.keepGoing")}</p>
              <button data-testid="celebration-close" onClick={() => setCelebration(null)} className="kc-btn-primary mt-5 !px-6 !py-2.5 w-full">
                {t("pdf.close")}
              </button>
            </div>
          </div>
        )}
        {!program || program?.is_sample ? (
          <section className="kc-card-dark p-6 sm:p-8 kc-animate" data-testid="trial-welcome-banner">
            <p className="kc-eyebrow !text-accent mb-1">{t("trial.welcome")}</p>
            <h1 className="text-3xl sm:text-4xl uppercase leading-tight text-[#F5E6E8]" data-testid="trial-welcome-title">{user.coach_id ? t("trial.preparingTitle") : t("trial.exploreTitle")}</h1>
            <p className="text-[#F5E6E8]/70 mt-3 text-sm max-w-md">{isTrial ? (user.coach_id ? t("trial.preparingText") : t("trial.exploreText")) : t("trial.preparingTextPaid")}</p>
            <div className="flex items-center gap-3 mt-5 flex-wrap">
              <span className="kc-badge bg-accent/15 text-accent border border-accent/40" data-testid="trial-days-badge">
                {isTrial ? `${t("trial.badge")} · ` : ""}{daysLeft} {t("client.daysLeft")}
              </span>
              {user.coach_id && (
                <Link to="/app/chat" data-testid="trial-chat-cta" className="kc-btn-gold !px-5 !py-2 text-sm inline-flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" /> {t("trial.chatCta")}
                </Link>
              )}
              {program?.is_sample && (
                <Link to="/app/track" data-testid="trial-sample-cta" className="kc-btn-primary !px-5 !py-2 text-sm inline-flex items-center gap-2">
                  <PlayCircle className="w-4 h-4" /> {t("trial.sampleCta")}
                </Link>
              )}
            </div>
          </section>
        ) : (
          <section className="kc-card relative overflow-hidden border-[#453740] kc-animate" data-testid="current-phase-banner">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-transparent to-accent/10 pointer-events-none" />
          <div className="relative p-6 sm:p-8">
            <p className="kc-eyebrow !text-accent mb-1">{program?.name || t("brand")}</p>
            <h1 className="text-4xl sm:text-6xl uppercase leading-[0.95] text-[#F5E6E8]" data-testid="hero-phase-title">
              {phase?.name || `${t("coach.phase")} ${current_phase_idx + 1}`}
            </h1>
            {phase?.goal && <p className="text-[#F5E6E8]/60 mt-2">{t("client.goal")}: {phase.goal}</p>}
            <p className="text-sm mt-1 font-mono text-accent">{currentWeek?.name}{currentWeek?.focus ? ` · ${currentWeek.focus}` : ""}</p>
            <div className="flex items-center gap-4 mt-6 flex-wrap">
              <span className="font-mono text-3xl font-black text-accent" data-testid="hero-progress">{progress}%</span>
              <Link data-testid="start-tracking-button" to="/app/track" className="kc-btn-primary !px-6 !py-2.5 text-sm inline-flex items-center gap-2">
                <PlayCircle className="w-4 h-4" /> {t("client.continue")}
              </Link>
              {program?._id && (
                <button data-testid="download-program-pdf" onClick={() => setPdfUrl(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${program._id}/pdf`)}
                  className="kc-btn-ghost !px-4 !py-2.5 text-sm inline-flex items-center gap-2 !text-[#F5E6E8] !border-[#453740]">
                  <Download className="w-4 h-4" /> {t("client.downloadPdf")}
                </button>
              )}
            </div>
            <div className="flex gap-1.5 mt-5" data-testid="week-dots">
              {weeks.map((w, i) => (
                <span key={w.id || i} className={`h-1.5 rounded-full transition-all ${i === current_week_idx ? "w-8 bg-primary" : i < current_week_idx ? "w-4 bg-primary/50" : "w-4 bg-border"}`} />
              ))}
            </div>
          </div>
          <img src="/coach-cartoon-clipboard.png" alt="" data-testid="coach-cartoon-banner"
            className="absolute bottom-0 right-0 w-24 sm:w-36 pointer-events-none select-none drop-shadow-lg" />
          </section>
        )}

        {isLastWeek && (
          <Link to="/app/feedback" data-testid="feedback-banner"
            className="kc-card kc-card-hover border-primary/50 p-5 flex items-center gap-4 kc-animate">
            <img src="/emblem_track.png" alt="" className="w-14 h-14 object-contain shrink-0" />
            <span className="flex-1">
              <span className="block font-bold text-lg">{t("fb.title")}</span>
              <span className="block text-sm text-muted-foreground">{t("fb.lastWeek")}</span>
            </span>
            <ChevronRight className="w-5 h-5 text-primary shrink-0" />
          </Link>
        )}

        <section className="grid grid-cols-3 gap-3 sm:gap-4">
          {tiles.map((tile, i) => (
            <Link key={tile.to} to={tile.to} data-testid={tile.testid}
              className="kc-card kc-card-hover p-4 sm:p-6 flex flex-col items-center gap-2 text-center kc-animate"
              style={{ animationDelay: `${0.08 * (i + 1)}s` }}>
              <img src={tile.img} alt="" className="w-16 h-16 sm:w-20 sm:h-20 object-contain" />
              <span className="font-bold text-sm sm:text-base">{tile.label}</span>
              <span className="text-xs text-muted-foreground hidden sm:block">{tile.sub}</span>
            </Link>
          ))}
        </section>

        {measures.filter((m) => m.weight_kg != null).length >= 2 && (
          <Link to="/app/photos" data-testid="home-weight-card"
            className="kc-card kc-card-hover p-5 block kc-animate" style={{ animationDelay: "0.35s" }}>
            <div className="flex items-baseline justify-between">
              <span className="kc-eyebrow">{t("ms.chart")}</span>
              <span className="font-mono text-2xl font-black text-primary" data-testid="home-weight-latest">
                {measures[0].weight_kg} <span className="text-sm font-normal text-muted-foreground">kg</span>
              </span>
            </div>
            <WeightChart entries={measures} />
          </Link>
        )}

        <InstallApp />
        <PushToggle />
        <ReferralCard />

        <section>
          <h2 className="kc-eyebrow mb-3">{t("client.plans")}</h2>
          <div className="space-y-3">
            {rows.map((row, i) => {
              const inner = (
                <>
                  {row.img ? (
                    <img src={row.img} alt="" className="w-14 h-14 object-contain shrink-0" />
                  ) : (
                    <span className="w-14 h-14 rounded-full bg-primary/15 grid place-items-center shrink-0">
                      <row.icon className="w-7 h-7 text-primary" />
                    </span>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block font-bold text-lg leading-tight">{row.title}</span>
                    <span className="block text-sm text-muted-foreground truncate">{row.sub}</span>
                  </span>
                  <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                </>
              );
              const cls = "kc-card kc-card-hover p-4 flex items-center gap-4 w-full text-left kc-animate";
              const style = { animationDelay: `${0.1 * (i + 3)}s` };
              return row.scroll ? (
                <button key={row.testid} data-testid={row.testid} style={style} className={cls}
                  onClick={() => document.getElementById("weeks-section")?.scrollIntoView({ behavior: "smooth" })}>
                  {inner}
                </button>
              ) : (
                <Link key={row.testid} to={row.to} data-testid={row.testid} style={style} className={cls}>
                  {inner}
                </Link>
              );
            })}
          </div>
        </section>

        {!program ? null : (
          <section id="weeks-section">
            <h2 className="text-xl font-bold mb-3">{t("client.weeks")}</h2>
            <div className="space-y-2" data-testid="weeks-list">
              {weeks.map((w, i) => (
                <div key={w.id || i} className={`kc-card overflow-hidden ${i === current_week_idx ? "border-primary/60" : ""}`}>
                  <button data-testid={`week-toggle-${i}`} onClick={() => setOpenWeek(openWeek === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 text-left">
                    <div>
                      <span className="font-bold">{w.name}</span>
                      <span className="text-sm text-muted-foreground ml-2">{w.monthName}{w.focus ? ` — ${w.focus}` : ""}</span>
                    </div>
                    {i === current_week_idx && <span className="kc-badge bg-primary/15 text-primary">{t("client.currentPhase")}</span>}
                  </button>
                  {openWeek === i && (
                    <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                      {(w.routines || []).map((r) => (
                        <div key={r.id || r.name || i} className="bg-secondary/40 rounded-lg p-3" data-testid={`routine-card-${r.id || i}`}>
                          <p className="font-bold text-sm">{r.name}{r.day ? ` · ${r.day}` : ""}</p>
                          <ul className="text-sm text-muted-foreground mt-1 space-y-0.5">
                            {(r.exercises || []).map((ex, j) => (
                              <li key={j} className="flex justify-between">
                                <span>{ex.name}</span>
                                <span className="font-mono">{ex.sets}×{ex.reps}{ex.rest_seconds ? ` · ${ex.rest_seconds}s` : ""}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <p className="text-xs text-muted-foreground text-center" data-testid="coach-card">
          {t("client.coach")}: {coach?.name || "—"} · {coach?.email || ""} · {t("client.package")}: {daysLeft} {t("client.daysLeft")}
        </p>
      </div>
    </ClientLayout>
  );
}
